import { TokenResponse } from '@sanlam-glacier/app-common/types/auth/token-response.type.js';

type LocalJwtProviderResponse = {
  error?: string;
  jwt_token?: string;
};

type InitLocalJwtProps = {
  localJwtProvider: string;
  gatewayToken: TokenResponse;
  bffApiUrl: string;
};

const LOCAL_JWT_HEADER_ITEM_NAME = 'local_jwt_token_header';
const LOCAL_JWT_EXPIRY_ITEM_NAME = 'local_jwt_token_expiry';

export const initLocalJwtToken = async ({
  localJwtProvider,
  gatewayToken,
  bffApiUrl
}: InitLocalJwtProps) => {
  const isLocalJwtNeeded =
    (bffApiUrl.startsWith('http://localhost') || bffApiUrl.startsWith('https://localhost')) &&
    !!localJwtProvider;
  if (!isLocalJwtNeeded) return '';

  const gatewayAuthHeader = `${gatewayToken.token_type} ${gatewayToken.access_token}`;

  const storedAuthTokenHeader = getLocalJwtFromSessionStorage();
  if (storedAuthTokenHeader) return storedAuthTokenHeader;

  const localJwtToken = await fetch(localJwtProvider, {
    headers: [['Authorization', gatewayAuthHeader]]
  }).then(
    (response): LocalJwtProviderResponse =>
      response.ok
        ? (response.json() as LocalJwtProviderResponse)
        : {
            error: 'Error occured fetching the local JWT token.'
          }
  );
  if (localJwtToken.error || !localJwtToken.jwt_token) return '';
  const localJwt = `${gatewayToken.token_type} ${localJwtToken.jwt_token}`;

  const expiryDate = new Date();
  expiryDate.setSeconds(expiryDate.getSeconds() + gatewayToken.expires_in);
  window.sessionStorage.setItem(LOCAL_JWT_HEADER_ITEM_NAME, localJwt);
  window.sessionStorage.setItem(LOCAL_JWT_EXPIRY_ITEM_NAME, expiryDate.toISOString());

  return localJwt;
};

export const getLocalJwtFromSessionStorage = (): string | undefined => {
  const expiryItem = window.sessionStorage.getItem(LOCAL_JWT_EXPIRY_ITEM_NAME);
  if (!expiryItem) return;
  const expiry = Date.parse(expiryItem);
  if (Date.now() > expiry) {
    window.sessionStorage.removeItem(LOCAL_JWT_HEADER_ITEM_NAME);
    window.sessionStorage.removeItem(LOCAL_JWT_EXPIRY_ITEM_NAME);
    return;
  }
  return window.sessionStorage.getItem(LOCAL_JWT_HEADER_ITEM_NAME) ?? undefined;
};
